import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Avatar,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  AccessTime as DeadlineIcon,
  Comment as CommentIcon,
  MoreVert as MoreIcon,
  Flag as FlagIcon,
} from '@mui/icons-material';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { format, isPast, isToday } from 'date-fns';
import { vi } from 'date-fns/locale';

const PRIORITY_CONFIG = {
  low: { color: '#94a3b8', label: 'Thấp', bgColor: '#f1f5f9' },
  medium: { color: '#3b82f6', label: 'Trung bình', bgColor: '#dbeafe' },
  high: { color: '#f59e0b', label: 'Cao', bgColor: '#fef3c7' },
  urgent: { color: '#ef4444', label: 'Khẩn cấp', bgColor: '#fee2e2' },
};

const CATEGORY_CONFIG = {
  marketing: { color: '#8b5cf6', label: 'Marketing' },
  social: { color: '#06b6d4', label: 'Social Media' },
  content: { color: '#10b981', label: 'Content' },
  production: { color: '#f59e0b', label: 'Sản xuất' },
  report: { color: '#6366f1', label: 'Báo cáo' },
};

const TaskCard = ({ task, onClick, onMenuClick }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: task._id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const priorityConfig = PRIORITY_CONFIG[task.priority] || PRIORITY_CONFIG.medium;
  const categoryConfig = CATEGORY_CONFIG[task.category] || CATEGORY_CONFIG.marketing;
  
  const isOverdue = task.deadline && isPast(new Date(task.deadline)) && task.status !== 'done';
  const isDueToday = task.deadline && isToday(new Date(task.deadline));

  const handleCardClick = (e) => {
    if (!isDragging) {
      onClick?.(task);
    }
  };

  const handleMenuClick = (e) => {
    e.stopPropagation();
    onMenuClick?.(e, task);
  };

  return (
    <Card
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      onClick={handleCardClick}
      sx={{
        mb: 1.5,
        borderRadius: 2.5,
        boxShadow: isDragging 
          ? '0 12px 28px rgba(0,0,0,0.2)' 
          : '0 2px 8px rgba(0,0,0,0.06)',
        border: isOverdue 
          ? '2px solid #fca5a5' 
          : isDueToday 
            ? '2px solid #fcd34d'
            : '1px solid #e2e8f0',
        bgcolor: isOverdue ? '#fef2f2' : 'white',
        cursor: 'grab',
        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
          boxShadow: '0 6px 20px rgba(0,0,0,0.12)',
          transform: 'translateY(-3px)',
          borderColor: '#667eea',
        },
        '&:active': {
          cursor: 'grabbing',
        },
        overflow: 'visible',
      }}
    >
      <CardContent sx={{ p: 2, pb: '16px !important' }}>
        {/* Header: Category & Priority */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5, gap: 0.5 }}>
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', flex: 1, minWidth: 0 }}>
            <Chip
              label={categoryConfig.label}
              size="small"
              sx={{
                height: 22,
                fontSize: '0.68rem',
                fontWeight: 600,
                bgcolor: categoryConfig.color + '15',
                color: categoryConfig.color,
                border: `1px solid ${categoryConfig.color}30`,
                '& .MuiChip-label': { px: 1 },
              }}
            />
            <Chip
              icon={<FlagIcon sx={{ fontSize: '11px !important' }} />}
              label={priorityConfig.label}
              size="small"
              sx={{
                height: 22,
                fontSize: '0.68rem',
                fontWeight: 600,
                bgcolor: priorityConfig.bgColor,
                color: priorityConfig.color,
                border: `1px solid ${priorityConfig.color}30`,
                '& .MuiChip-icon': { color: priorityConfig.color, ml: 0.5 },
                '& .MuiChip-label': { px: 0.75 },
              }}
            />
          </Box>
          
          <IconButton 
            size="small" 
            onClick={handleMenuClick}
            sx={{ 
              p: 0.5,
              ml: 0.5,
              opacity: 0.5,
              flexShrink: 0,
              '&:hover': { opacity: 1, bgcolor: 'rgba(0,0,0,0.04)' }
            }}
          >
            <MoreIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Title */}
        <Tooltip title={task.title} placement="top" arrow enterDelay={500}>
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 600,
              mb: 1,
              lineHeight: 1.45,
              fontSize: '0.875rem',
              color: '#1e293b',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              wordBreak: 'break-word',
            }}
          >
            {task.title}
          </Typography>
        </Tooltip>

        {/* Description Preview */}
        {task.description && (
          <Typography
            variant="body2"
            sx={{
              mb: 1.5,
              fontSize: '0.78rem',
              lineHeight: 1.5,
              color: '#64748b',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              wordBreak: 'break-word',
            }}
          >
            {task.description}
          </Typography>
        )}

        {/* Footer: Deadline, Comments, Assignee */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {/* Deadline */}
            {task.deadline && (
              <Tooltip title={`Deadline: ${format(new Date(task.deadline), 'dd/MM/yyyy HH:mm', { locale: vi })}`}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    px: 1,
                    py: 0.25,
                    borderRadius: 1,
                    bgcolor: isOverdue ? '#fee2e2' : isDueToday ? '#fef3c7' : '#f1f5f9',
                  }}
                >
                  <DeadlineIcon 
                    sx={{ 
                      fontSize: 14, 
                      color: isOverdue ? '#ef4444' : isDueToday ? '#f59e0b' : '#64748b' 
                    }} 
                  />
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 500,
                      color: isOverdue ? '#ef4444' : isDueToday ? '#f59e0b' : '#64748b',
                    }}
                  >
                    {format(new Date(task.deadline), 'dd/MM', { locale: vi })}
                  </Typography>
                </Box>
              </Tooltip>
            )}

            {/* Comments Count */}
            {task.comments && task.comments.length > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <CommentIcon sx={{ fontSize: 14, color: '#64748b' }} />
                <Typography variant="caption" color="text.secondary">
                  {task.comments.length}
                </Typography>
              </Box>
            )}
          </Box>

          {/* Assignee */}
          {task.assignee && (
            <Tooltip title={task.assignee.fullName || 'Chưa giao'}>
              <Avatar
                src={task.assignee.avatar}
                sx={{
                  width: 24,
                  height: 24,
                  fontSize: '0.7rem',
                  bgcolor: '#667eea',
                }}
              >
                {(task.assignee.fullName || 'U')[0].toUpperCase()}
              </Avatar>
            </Tooltip>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default TaskCard;
